import React, { useEffect, useState, useCallback } from "react";
import api from "../api";
import { Link } from "react-router-dom";
import { Users, FileText, Share2, TrendingUp, Activity, Search, ChevronLeft, ChevronRight, Mail, Calendar, Download, Briefcase, Inbox, BarChart3, Sparkles, PieChart, Heart, Cake, FileSignature, Database, CheckCircle2, XCircle, RefreshCw, Play, HardDrive } from "lucide-react";

const PAGE_SIZE = 20;

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [insights, setInsights] = useState(null);
  const [users, setUsers] = useState({ total: 0, users: [] });
  const [newsletter, setNewsletter] = useState(null);
  const [partners, setPartners] = useState(null);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [page, setPage] = useState(0);
  const [managingUser, setManagingUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/kpis").then((r) => setKpis(r.data)).catch(() => setKpis(null));
    api.get("/admin/insights").then((r) => setInsights(r.data)).catch(() => setInsights(null));
    api.get("/admin/newsletter").then((r) => setNewsletter(r.data)).catch(() => setNewsletter({ subscribers: [] }));
    api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => setPartners({ leads: [] }));
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({
        search, plan: planFilter,
        skip: page * PAGE_SIZE, limit: PAGE_SIZE,
      });
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data);
    } finally {
      setLoadingUsers(false);
    }
  }, [search, planFilter, page]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [search, planFilter]);

  if (!stats) return <div>Chargement…</div>;

  const totalPages = Math.max(1, Math.ceil(users.total / PAGE_SIZE));
  const planBadgeColor = {
    decouverte: { bg: "rgba(35,38,46,0.08)", color: "var(--auprea-ink)" },
    essentiel: { bg: "rgba(29,58,114,0.12)", color: "var(--auprea-blue)" },
    confort: { bg: "rgba(202,168,106,0.2)", color: "#8a6f3d" },
  };
  const statusLabel = { active: "Actif", canceled: "Résilié", free: "Gratuit" };

  return (
    <div className="space-y-8">
      <div>
        <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
          — Administration
        </div>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }} data-testid="admin-title">Tableau de bord</h1>
            <p style={{ fontSize: 15, color: "rgba(35,38,46,0.6)", marginTop: 8 }}>
              Vue d'ensemble de vos clients. Le contenu détaillé des carnets n'est jamais exposé.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/heritier" className="auprea-btn flex items-center gap-2 justify-center" data-testid="admin-link-heritier" style={{ whiteSpace: "nowrap", background: "#fff", color: "var(--auprea-navy)", border: "1px solid var(--auprea-navy)" }}>
              <Briefcase size={16} /> Demandes héritier
            </Link>
            <Link to="/admin/partenaires" className="auprea-btn flex items-center gap-2 justify-center" data-testid="admin-link-partenaires" style={{ whiteSpace: "nowrap" }}>
              <Briefcase size={16} /> Annuaire pro
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={<Users />} label="Comptes actifs" value={stats.total_carnets} testid="stat-carnets" />
        <Stat icon={<Activity />} label="Taux de complétion" value={`${stats.avg_completion}%`} testid="stat-completion" />
        <Stat icon={<Share2 />} label="Partages actifs" value={stats.active_shares} testid="stat-shares" />
        <Stat icon={<TrendingUp />} label="MRR (€)" value={stats.mrr.toFixed(2).replace(".", ",")} testid="stat-mrr" />
      </div>

      {/* === KPI Marketing & Funnel === */}
      {kpis && <KpiSection kpis={kpis} />}

      {/* === Insights (data viz patrimoniale) === */}
      {insights && <InsightsSection insights={insights} />}

      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(stats.plans_distribution).map(([k, v]) => (
          <div key={k} className="auprea-card" data-testid={`plan-dist-${k}`}>
            <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Forfait {k}</div>
            <div style={{ fontFamily: "Spectral, serif", fontSize: 32, color: "var(--auprea-navy)", marginTop: 4 }}>{v}</div>
            <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>compte{v > 1 ? "s" : ""}</div>
          </div>
        ))}
      </div>

      {/* Repartition geographique (clients payants uniquement) */}
      {stats.departements_paid && stats.departements_paid.length > 0 && (
        <div className="auprea-card" data-testid="dept-distribution">
          <h2 style={{ fontSize: 22, marginBottom: 4 }}>Répartition géographique</h2>
          <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 16 }}>
            Clients payants (Essentiel + Confort) par département (préfixe du code postal).
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {stats.departements_paid.map((d) => (
              <div key={d.dept} className="flex items-center justify-between" style={{
                background: "rgba(202,168,106,0.08)",
                border: "1px solid rgba(202,168,106,0.3)",
                padding: "10px 14px",
              }} data-testid={`dept-${d.dept}`}>
                <span style={{ fontFamily: "Spectral, serif", fontSize: 18, color: "var(--auprea-navy)", fontWeight: 600 }}>
                  {d.dept}
                </span>
                <span style={{ fontSize: 13, color: "rgba(35,38,46,0.7)" }}>
                  {d.count} client{d.count > 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full clients list */}
      <div className="auprea-card">

        {managingUser && (
          <ManagePlanModal
            u={managingUser}
            onClose={() => setManagingUser(null)}
            onUpdated={() => { setManagingUser(null); loadUsers(); }}
          />
        )}

        {/* Section Sauvegardes OVH */}
        <BackupsSection />

        {/* Section Bilans Succession */}
        <BilansSuccessionSection />

        {/* Section Ouvrir une succession (module dédié) */}
        <SuccessionsSection />

        {/* Section Newsletter */}
        <NewsletterSection newsletter={newsletter} />

        {/* Section Demandes partenaires */}
        <PartnerLeadsSection partners={partners} />

        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div>
            <h2 style={{ fontSize: 22 }}>Tous les clients</h2>
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.6)" }}>
              {users.total} compte{users.total > 1 ? "s" : ""} au total
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div style={{ position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "rgba(35,38,46,0.4)" }} />
              <input
                className="auprea-input"
                style={{ paddingLeft: 36, minWidth: 240 }}
                placeholder="Rechercher (nom, email)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="admin-search"
              />
            </div>
            <select className="auprea-input" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} data-testid="admin-plan-filter">
              <option value="">Tous forfaits</option>
              <option value="decouverte">Découverte</option>
              <option value="essentiel">Essentiel</option>
              <option value="confort">Confort</option>
            </select>
            <ExportMenu
              testIdPrefix="export-clients"
              disabled={users.total === 0}
              onExport={(fmt) => {
                const qs = new URLSearchParams({ search, plan: planFilter }).toString();
                const date = new Date().toISOString().slice(0, 10);
                if (fmt === "csv") {
                  // CSV : on récupère toutes les lignes filtrées via /admin/users (sans pagination)
                  api.get(`/admin/users?${qs}&limit=5000`).then((r) => {
                    const all = r.data?.users || [];
                    const planLabel = { decouverte: "Découverte", essentiel: "Essentiel", confort: "Confort" };
                    const statusLbl = { active: "Actif", canceled: "Résilié", free: "Gratuit" };
                    const rows = all.map((u) => ({
                      full_name: u.full_name || "",
                      email: u.email || "",
                      plan_id: planLabel[u.plan_id] || u.plan_id || "",
                      subscription_status: statusLbl[u.subscription_status] || u.subscription_status || "—",
                      code_postal: u.code_postal || "",
                      ville: u.ville || "",
                      progression: u.progression ?? 0,
                      created_at: u.created_at ? new Date(u.created_at).toLocaleString("fr-FR") : "",
                    }));
                    downloadCSV(`auprea_clients_${date}.csv`, rows, [
                      { key: "full_name", label: "Nom complet" },
                      { key: "email", label: "Email" },
                      { key: "plan_id", label: "Forfait" },
                      { key: "subscription_status", label: "Statut" },
                      { key: "code_postal", label: "Code postal" },
                      { key: "ville", label: "Ville" },
                      { key: "progression", label: "Progression (%)" },
                      { key: "created_at", label: "Inscrit le" },
                    ]);
                  }).catch(() => alert("L'export a échoué. Réessayez."));
                } else {
                  downloadFromApi(`/api/admin/export/clients.xlsx?${qs}`, `auprea_clients_${date}.xlsx`);
                }
              }}
            />
          </div>
        </div>

        {loadingUsers ? (
          <div style={{ padding: 24, textAlign: "center", color: "rgba(35,38,46,0.6)" }}>Chargement…</div>
        ) : users.users.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "rgba(35,38,46,0.6)" }} data-testid="admin-users-empty">
            Aucun client ne correspond à ces critères.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="admin-users-table">
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(21,41,79,0.15)" }}>
                  <th style={thStyle}>Nom complet</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Forfait</th>
                  <th style={thStyle}>Statut</th>
                  <th style={thStyle}>Localisation</th>
                  <th style={thStyle}>Progression</th>
                  <th style={thStyle}>Inscrit le</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.users.map((u) => (
                  <tr key={u.id} style={{ borderBottom: "1px solid rgba(21,41,79,0.06)" }} data-testid={`admin-user-row-${u.email}`}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>{u.full_name || "—"}</div>
                    </td>
                    <td style={tdStyle}>
                      <a href={`mailto:${u.email}`} style={{ color: "var(--auprea-blue)", fontSize: 14 }}>
                        <Mail size={12} style={{ display: "inline", marginRight: 4 }} />
                        {u.email}
                      </a>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        ...planBadgeColor[u.plan_id] || planBadgeColor.decouverte,
                        padding: "3px 10px", borderRadius: 0, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                      }}>{u.plan_id}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 13, color: u.subscription_status === "active" ? "var(--auprea-green)" : "rgba(35,38,46,0.6)" }}>
                        {statusLabel[u.subscription_status] || u.subscription_status || "—"}
                      </span>
                      {u.current_period_end && (
                        <div style={{ fontSize: 11, color: "rgba(35,38,46,0.5)" }}>
                          jusqu'au {new Date(u.current_period_end).toLocaleDateString("fr-FR")}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {u.code_postal || u.ville ? (
                        <div>
                          <div style={{ fontFamily: "Spectral, serif", fontSize: 15, color: "var(--auprea-navy)" }}>
                            {u.code_postal || "—"}
                          </div>
                          {u.ville && (
                            <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{u.ville}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: "rgba(35,38,46,0.4)" }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontSize: 13 }}>{u.progression}%</div>
                      <div style={{ background: "rgba(21,41,79,0.1)", height: 4, width: 80, marginTop: 4 }}>
                        <div style={{ background: "var(--auprea-gold)", height: 4, width: `${u.progression}%` }} />
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 13, color: "rgba(35,38,46,0.7)" }}>
                        <Calendar size={11} style={{ display: "inline", marginRight: 4 }} />
                        {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => setManagingUser(u)}
                        style={{ fontSize: 12, color: "var(--auprea-blue)", textDecoration: "underline", cursor: "pointer" }}
                        data-testid={`btn-manage-${u.email}`}
                      >
                        Gérer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {users.total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
            <div style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, users.total)} sur {users.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="auprea-btn-outline" style={{ padding: "8px 12px", minHeight: "auto" }}
                data-testid="admin-prev-page"
              >
                <ChevronLeft size={16} />
              </button>
              <span style={{ alignSelf: "center", fontSize: 13 }}>Page {page + 1}/{totalPages}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= users.total}
                className="auprea-btn-outline" style={{ padding: "8px 12px", minHeight: "auto" }}
                data-testid="admin-next-page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="auprea-card" data-testid={testid}>
      <div style={{ color: "var(--auprea-gold)" }}>{icon}</div>
      <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 12 }}>{label}</div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 32, color: "var(--auprea-navy)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "12px 8px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--auprea-navy)",
};

const tdStyle = {
  padding: "12px 8px",
  fontSize: 14,
  verticalAlign: "top",
};

function downloadCSV(filename, rows, headers) {
  const csv = [
    headers.map((h) => `"${h.label}"`).join(","),
    ...rows.map((r) => headers.map((h) => `"${(r[h.key] ?? "").toString().replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 500);
}

/**
 * Télécharge un fichier via un endpoint API en préservant les cookies (auth).
 * `path` est le chemin relatif à REACT_APP_BACKEND_URL, ex. "/api/admin/export/clients.xlsx"
 */
async function downloadFromApi(path, fallbackName = "export.xlsx") {
  const base = process.env.REACT_APP_BACKEND_URL;
  const url = `${base}${path}`;
  try {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) {
      const t = await resp.text().catch(() => "");
      throw new Error(t || `HTTP ${resp.status}`);
    }
    // Extract filename from Content-Disposition if present
    const cd = resp.headers.get("Content-Disposition") || "";
    let filename = fallbackName;
    const m = /filename="?([^"]+)"?/i.exec(cd);
    if (m && m[1]) filename = m[1];
    const blob = await resp.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 500);
  } catch (err) {
    console.error("[export] download failed", err);
    alert("L'export a échoué. Vérifiez votre connexion admin puis réessayez.");
  }
}

/**
 * Bouton dropdown "Exporter ▾" avec choix CSV ou Excel.
 * onExport(format) : callback qui reçoit "csv" ou "xlsx"
 */
function ExportMenu({ onExport, testIdPrefix = "export", disabled = false, label = "Exporter" }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        className="auprea-btn-outline"
        style={{ padding: "8px 14px", minHeight: "auto", fontSize: 13, opacity: disabled ? 0.5 : 1 }}
        data-testid={`btn-${testIdPrefix}-toggle`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Download size={14} style={{ marginRight: 6 }} /> {label} <span style={{ marginLeft: 4 }}>▾</span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            background: "#fff",
            border: "1px solid rgba(21,41,79,0.15)",
            minWidth: 200,
            boxShadow: "0 6px 22px rgba(21,41,79,0.12)",
            zIndex: 40,
          }}
          data-testid={`menu-${testIdPrefix}`}
        >
          <button
            onClick={() => { setOpen(false); onExport("csv"); }}
            className="w-full text-left"
            style={{ padding: "10px 14px", fontSize: 13, borderBottom: "1px solid rgba(21,41,79,0.08)", color: "var(--auprea-ink)" }}
            data-testid={`btn-${testIdPrefix}-csv`}
          >
            Exporter en CSV
          </button>
          <button
            onClick={() => { setOpen(false); onExport("xlsx"); }}
            className="w-full text-left"
            style={{ padding: "10px 14px", fontSize: 13, color: "var(--auprea-ink)" }}
            data-testid={`btn-${testIdPrefix}-xlsx`}
          >
            Exporter en Excel (.xlsx)
          </button>
        </div>
      )}
    </div>
  );
}

// ===== KPI Marketing section =====
function KpiSection({ kpis }) {
  const { funnel, completion_buckets, conversion, emails, monthly_signups } = kpis;
  const maxMonthly = Math.max(1, ...monthly_signups.map((m) => m.count));
  const totalCarnets = funnel.total_inscrits || 1;

  return (
    <div className="auprea-card" data-testid="admin-kpi-section">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 size={22} style={{ color: "var(--auprea-gold)" }} />
        <div>
          <h2 style={{ fontSize: 22, margin: 0 }}>KPI Marketing &amp; Funnel</h2>
          <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", margin: 0 }}>
            Vue d'ensemble du parcours utilisateur, de l'inscription à la conversion.
          </p>
        </div>
      </div>

      {/* Funnel : 3 cartes principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KpiCard
          label="Inscrits au total"
          value={funnel.total_inscrits}
          accent="navy"
          testid="kpi-total"
        />
        <KpiCard
          label="Ont commencé leur carnet"
          value={funnel.ont_commence}
          sub={`${funnel.activation_rate_pct}% d'activation`}
          accent="gold"
          testid="kpi-activation"
        />
        <KpiCard
          label="Carnets terminés (100%)"
          value={funnel.ont_termine}
          sub={`${funnel.completion_rate_pct}% de complétion`}
          accent="green"
          testid="kpi-completion"
        />
      </div>

      {/* Distribution complétion + Conversion forfait */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div>
          <div style={kpiSubtitle}>Distribution complétion</div>
          <div className="space-y-2 mt-3" data-testid="kpi-buckets">
            {Object.entries(completion_buckets).map(([label, count]) => {
              const pct = totalCarnets ? (count / totalCarnets) * 100 : 0;
              return (
                <div key={label}>
                  <div className="flex justify-between" style={{ fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: "var(--auprea-ink)", fontWeight: 500 }}>{label}</span>
                    <span style={{ color: "rgba(35,38,46,0.7)" }}>
                      {count} <span style={{ color: "rgba(35,38,46,0.5)" }}>· {pct.toFixed(0)}%</span>
                    </span>
                  </div>
                  <div style={{ height: 8, background: "rgba(21,41,79,0.06)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: label === "100%" ? "var(--auprea-green)" : "var(--auprea-gold)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div style={kpiSubtitle}>Conversion forfait</div>
          <div className="grid grid-cols-3 gap-2 mt-3" data-testid="kpi-conversion">
            {Object.entries(conversion.plan_counts).map(([k, v]) => (
              <div key={k} style={{
                background: "rgba(202,168,106,0.06)",
                border: "1px solid rgba(202,168,106,0.2)",
                padding: "14px 12px",
                textAlign: "center",
              }}>
                <div style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>{k}</div>
                <div style={{ fontFamily: "Spectral, serif", fontSize: 26, color: "var(--auprea-navy)", marginTop: 4 }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="flex items-center justify-between" style={{ padding: "10px 12px", background: "rgba(29,58,114,0.06)", fontSize: 13 }}>
              <span style={{ color: "rgba(35,38,46,0.7)" }}>Taux d'abonnement</span>
              <span style={{ fontFamily: "Spectral, serif", color: "var(--auprea-blue)", fontSize: 16, fontWeight: 600 }}>{conversion.conversion_paid_pct}%</span>
            </div>
            <div className="flex items-center justify-between" style={{ padding: "10px 12px", background: "rgba(202,168,106,0.08)", fontSize: 13 }}>
              <span style={{ color: "rgba(35,38,46,0.7)" }}>Part Confort</span>
              <span style={{ fontFamily: "Spectral, serif", color: "#8a6f3d", fontSize: 16, fontWeight: 600 }}>{conversion.conversion_confort_pct}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Emails envoyés */}
      <div className="mb-8">
        <div style={kpiSubtitle}>Emails automatiques envoyés</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3" data-testid="kpi-emails">
          <EmailKpi label="Bienvenue" value={emails.welcome_sent} icon={<Sparkles size={14} />} />
          <EmailKpi label="Relance J+3" value={emails.reminder_j3_sent} icon={<Mail size={14} />} />
          <EmailKpi label="Relance J+7" value={emails.reminder_j7_sent} icon={<Mail size={14} />} />
          <EmailKpi label="Félicitations 100%" value={emails.completion_sent} icon={<Sparkles size={14} />} accent="green" />
          <EmailKpi label="Reset MdP demandés" value={emails.password_reset_requested} icon={<Mail size={14} />} />
          <EmailKpi label="Reset MdP confirmés" value={emails.password_reset_completed} icon={<Mail size={14} />} />
          <EmailKpi label="Inscrits newsletter" value={emails.newsletter_subscribers} icon={<Inbox size={14} />} />
          <EmailKpi label="Leads partenaires" value={emails.partner_leads} icon={<Briefcase size={14} />} />
        </div>
        <p style={{ fontSize: 11, color: "rgba(35,38,46,0.5)", marginTop: 10, fontStyle: "italic" }}>
          Données envoi confirmées via SendGrid. Les taux d'ouverture ne sont pas trackés (respect RGPD : pas de pixel espion).
        </p>
      </div>

      {/* Inscriptions par mois */}
      <div>
        <div style={kpiSubtitle}>Inscriptions par mois (6 derniers)</div>
        <div className="mt-4 flex items-end gap-3" style={{ height: 140 }} data-testid="kpi-monthly">
          {monthly_signups.map((m, i) => {
            const h = (m.count / maxMonthly) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div style={{ fontSize: 12, color: "var(--auprea-navy)", fontWeight: 600, marginBottom: 4 }}>{m.count}</div>
                <div style={{
                  width: "100%",
                  height: `${Math.max(2, h)}%`,
                  background: i === monthly_signups.length - 1 ? "var(--auprea-gold)" : "rgba(21,41,79,0.7)",
                  borderRadius: "4px 4px 0 0",
                  transition: "all 0.3s ease",
                  minHeight: 2,
                }} />
                <div style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", marginTop: 6, textTransform: "lowercase" }}>{m.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent = "navy", testid }) {
  const colors = {
    navy: { bg: "rgba(21,41,79,0.04)", border: "rgba(21,41,79,0.15)", value: "var(--auprea-navy)" },
    gold: { bg: "rgba(202,168,106,0.08)", border: "rgba(202,168,106,0.3)", value: "#8a6f3d" },
    green: { bg: "rgba(47,125,91,0.06)", border: "rgba(47,125,91,0.25)", value: "var(--auprea-green)" },
  }[accent];
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, padding: "18px 20px" }} data-testid={testid}>
      <div style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 36, color: colors.value, marginTop: 6, lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)", marginTop: 6 }}>{sub}</div>
      )}
    </div>
  );
}

function EmailKpi({ label, value, icon, accent }) {
  const color = accent === "green" ? "var(--auprea-green)" : "var(--auprea-navy)";
  return (
    <div style={{ padding: "12px 14px", background: "rgba(243,239,230,0.6)", border: "1px solid rgba(21,41,79,0.08)" }}>
      <div className="flex items-center gap-1" style={{ color: "rgba(35,38,46,0.6)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
        {icon} <span>{label}</span>
      </div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 24, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

const kpiSubtitle = {
  fontSize: 12,
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  fontWeight: 600,
  color: "rgba(35,38,46,0.6)",
};

// ===== Insights (data viz patrimoniale) =====
function InsightsSection({ insights }) {
  const totalForPyramid = insights.age_pyramid.reduce((s, x) => s + x.count, 0) || 1;
  const totalForStatut = insights.statut_couple.reduce((s, x) => s + x.count, 0) || 1;
  const totalForObs = insights.type_obseques.reduce((s, x) => s + x.count, 0) || 1;
  const totalForDon = insights.don_organes.reduce((s, x) => s + x.count, 0) || 1;
  const maxDept = Math.max(1, ...insights.departements.map((d) => d.count));

  return (
    <div className="auprea-card" data-testid="admin-insights-section">
      <div className="flex items-center gap-3 mb-6">
        <PieChart size={22} style={{ color: "var(--auprea-gold)" }} />
        <div>
          <h2 style={{ fontSize: 22, margin: 0 }}>Insights patrimoniaux</h2>
          <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", margin: 0 }}>
            Analyse agrégée du portefeuille (jamais nominatif, basée sur les champs structurés).
          </p>
        </div>
      </div>

      {/* Indicateurs clés en haut */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <InsightStat icon={<FileSignature size={16} />} label="Ont un testament" value={`${insights.testament.pct_oui}%`} sub={`${insights.testament.oui} / ${insights.total_users}`} accent="navy" testid="insight-testament" />
        <InsightStat icon={<FileSignature size={16} />} label="Mandat protection" value={`${insights.mandat_protection.pct_oui}%`} sub={`${insights.mandat_protection.oui} / ${insights.total_users}`} accent="navy" testid="insight-mandat" />
        <InsightStat icon={<TrendingUp size={16} />} label="Assurance-vie" value={`${insights.assurance_vie.pct_oui}%`} sub={`${insights.assurance_vie.oui} / ${insights.total_users}`} accent="gold" testid="insight-av" />
        <InsightStat icon={<TrendingUp size={16} />} label="Crédit immo en cours" value={`${insights.credit_immo.pct_oui}%`} sub={`${insights.credit_immo.oui} / ${insights.total_users}`} accent="gold" testid="insight-credit" />
      </div>

      {/* Pyramide des âges */}
      <div className="mb-8">
        <div style={kpiSubtitle}>
          <Cake size={12} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
          Pyramide des âges
        </div>
        <div className="space-y-2 mt-3" data-testid="insight-age-pyramid">
          {insights.age_pyramid.map((b) => {
            const pct = (b.count / totalForPyramid) * 100;
            return (
              <div key={b.label}>
                <div className="flex justify-between" style={{ fontSize: 13, marginBottom: 4 }}>
                  <span style={{ color: "var(--auprea-ink)", fontWeight: 500, width: 110, display: "inline-block" }}>{b.label}</span>
                  <span style={{ color: "rgba(35,38,46,0.7)" }}>
                    {b.count} <span style={{ color: "rgba(35,38,46,0.5)" }}>· {pct.toFixed(0)}%</span>
                  </span>
                </div>
                <div style={{ height: 10, background: "rgba(21,41,79,0.06)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    width: `${pct}%`,
                    height: "100%",
                    background: b.label === "Non renseigné" ? "rgba(35,38,46,0.25)" : "var(--auprea-navy)",
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Statut couple + Type obsèques + Don organes — 3 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <DistColumn title={<><Heart size={12} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} /> Statut de couple</>} items={insights.statut_couple} total={totalForStatut} color="var(--auprea-blue)" testid="insight-statut" empty="Statut non renseigné." />
        <DistColumn title="Type d'obsèques souhaité" items={insights.type_obseques} total={totalForObs} color="var(--auprea-gold)" testid="insight-obseques" empty="Non renseigné." />
        <DistColumn title="Don d'organes" items={insights.don_organes} total={totalForDon} color="var(--auprea-green)" testid="insight-don" empty="Non renseigné." />
      </div>

      {/* Montants par range (5 catégories) */}
      <div className="mb-8">
        <div style={kpiSubtitle}>Tranches de montants déclarés (items structurés)</div>
        <p style={{ fontSize: 12, color: "rgba(35,38,46,0.55)", margin: "6px 0 14px 0" }}>
          Agrégation de tous les items renseignés par les utilisateurs ({Object.entries(insights.items_counts).reduce((s, [, v]) => s + v, 0)} items au total).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="insight-montants">
          {Object.entries(insights.montants_distribution).map(([kind, dist]) => {
            const entries = Object.entries(dist).sort((a, b) => b[1] - a[1]);
            const total = entries.reduce((s, [, v]) => s + v, 0);
            const itemCount = insights.items_counts[kind] || 0;
            return (
              <div key={kind} style={{ background: "rgba(243,239,230,0.5)", padding: "14px 16px", border: "1px solid rgba(21,41,79,0.08)" }} data-testid={`insight-montants-${kind}`}>
                <div style={{ fontFamily: "Spectral, serif", fontSize: 16, color: "var(--auprea-navy)", marginBottom: 4, textTransform: "capitalize" }}>
                  {kind}
                </div>
                <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", marginBottom: 10 }}>
                  {itemCount} item{itemCount > 1 ? "s" : ""} renseigné{itemCount > 1 ? "s" : ""}
                </div>
                {entries.length === 0 ? (
                  <div style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>
                    Aucun montant renseigné pour l&apos;instant.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {entries.map(([range, count]) => {
                      const pct = (count / total) * 100;
                      return (
                        <div key={range}>
                          <div className="flex justify-between" style={{ fontSize: 12 }}>
                            <span style={{ color: "rgba(35,38,46,0.85)" }}>{range}</span>
                            <span style={{ color: "rgba(35,38,46,0.6)" }}>{count}</span>
                          </div>
                          <div style={{ height: 4, background: "rgba(21,41,79,0.06)" }}>
                            <div style={{ width: `${pct}%`, height: "100%", background: "var(--auprea-gold)" }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Heatmap départements */}
      {insights.departements.length > 0 && (
        <div>
          <div style={kpiSubtitle}>Heatmap départements (tous comptes)</div>
          <p style={{ fontSize: 12, color: "rgba(35,38,46,0.55)", margin: "6px 0 14px 0" }}>
            Préfixe du code postal renseigné dans la rubrique Identité.
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="insight-dept-heatmap">
            {insights.departements.map((d) => {
              const intensity = d.count / maxDept;
              const bg = `rgba(21,41,79,${0.08 + intensity * 0.55})`;
              const textColor = intensity > 0.55 ? "#fff" : "var(--auprea-navy)";
              return (
                <div key={d.dept} style={{
                  background: bg,
                  color: textColor,
                  padding: "10px 8px",
                  textAlign: "center",
                  borderRadius: 3,
                }} data-testid={`insight-dept-${d.dept}`}>
                  <div style={{ fontFamily: "Spectral, serif", fontSize: 17, fontWeight: 600 }}>{d.dept}</div>
                  <div style={{ fontSize: 11, opacity: 0.85 }}>{d.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function InsightStat({ icon, label, value, sub, accent = "navy", testid }) {
  const colors = {
    navy: { bg: "rgba(21,41,79,0.04)", border: "rgba(21,41,79,0.15)", value: "var(--auprea-navy)" },
    gold: { bg: "rgba(202,168,106,0.08)", border: "rgba(202,168,106,0.3)", value: "#8a6f3d" },
    green: { bg: "rgba(47,125,91,0.06)", border: "rgba(47,125,91,0.25)", value: "var(--auprea-green)" },
  }[accent];
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, padding: "14px 16px" }} data-testid={testid}>
      <div className="flex items-center gap-2" style={{ color: "rgba(35,38,46,0.6)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>
        {icon} <span>{label}</span>
      </div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 28, color: colors.value, marginTop: 4, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function DistColumn({ title, items, total, color, testid, empty }) {
  return (
    <div data-testid={testid}>
      <div style={kpiSubtitle}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: "rgba(35,38,46,0.55)", fontStyle: "italic", marginTop: 12 }}>{empty}</div>
      ) : (
        <div className="space-y-2 mt-3">
          {items.map((it) => {
            const pct = (it.count / total) * 100;
            return (
              <div key={it.label}>
                <div className="flex justify-between" style={{ fontSize: 12, marginBottom: 3 }}>
                  <span style={{ color: "var(--auprea-ink)" }}>{it.label}</span>
                  <span style={{ color: "rgba(35,38,46,0.6)" }}>
                    {it.count} <span style={{ color: "rgba(35,38,46,0.45)" }}>· {pct.toFixed(0)}%</span>
                  </span>
                </div>
                <div style={{ height: 6, background: "rgba(21,41,79,0.06)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewsletterSection({ newsletter }) {
  if (!newsletter) return null;
  const subs = newsletter.subscribers || [];
  return (
    <div className="mb-8 pb-8 border-b" style={{ borderColor: "rgba(21,41,79,0.1)" }} data-testid="admin-newsletter-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 style={{ fontSize: 22 }}>
            <Inbox size={20} style={{ display: "inline", marginRight: 8, color: "#caa86a" }} />
            Inscriptions newsletter
          </h2>
          <p style={{ fontSize: 14, color: "rgba(35,38,46,0.6)" }}>
            {subs.length} inscrit{subs.length > 1 ? "s" : ""} via le formulaire du pied de page
          </p>
        </div>
        {subs.length > 0 && (
          <ExportMenu
            testIdPrefix="export-newsletter"
            onExport={(fmt) => {
              if (fmt === "csv") {
                downloadCSV(`auprea_newsletter_${new Date().toISOString().slice(0,10)}.csv`, subs, [
                  { key: "email", label: "Email" },
                  { key: "source", label: "Source" },
                  { key: "created_at", label: "Date inscription" },
                ]);
              } else {
                downloadFromApi("/api/admin/export/newsletter.xlsx", "auprea_newsletter_export.xlsx");
              }
            }}
          />
        )}
      </div>
      {subs.length === 0 ? (
        <div style={{ padding: 20, color: "rgba(35,38,46,0.6)", fontSize: 14 }}>Aucune inscription pour l'instant.</div>
      ) : (
        <div style={{ maxHeight: 260, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(21,41,79,0.15)" }}>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Date</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.email} style={{ borderBottom: "1px solid rgba(21,41,79,0.06)" }}>
                  <td style={tdStyle}>
                    <a href={`mailto:${s.email}`} style={{ color: "var(--auprea-blue)" }}>{s.email}</a>
                  </td>
                  <td style={tdStyle}><span style={{ fontSize: 13, color: "rgba(35,38,46,0.7)" }}>{s.source || "—"}</span></td>
                  <td style={tdStyle}><span style={{ fontSize: 13, color: "rgba(35,38,46,0.7)" }}>{s.created_at ? new Date(s.created_at).toLocaleString("fr-FR") : "—"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PartnerLeadsSection({ partners }) {
  if (!partners) return null;
  const leads = partners.leads || [];
  return (
    <div className="mb-8 pb-8 border-b" style={{ borderColor: "rgba(21,41,79,0.1)" }} data-testid="admin-partners-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 style={{ fontSize: 22 }}>
            <Briefcase size={20} style={{ display: "inline", marginRight: 8, color: "#caa86a" }} />
            Demandes partenaires (notaires & CGP)
          </h2>
          <p style={{ fontSize: 14, color: "rgba(35,38,46,0.6)" }}>
            {leads.length} demande{leads.length > 1 ? "s" : ""} via la page <code>/pros</code>
          </p>
        </div>
        {leads.length > 0 && (
          <ExportMenu
            testIdPrefix="export-partners"
            onExport={(fmt) => {
              if (fmt === "csv") {
                downloadCSV(`auprea_partenaires_${new Date().toISOString().slice(0,10)}.csv`, leads, [
                  { key: "nom_complet", label: "Nom" },
                  { key: "email", label: "Email" },
                  { key: "profession", label: "Profession" },
                  { key: "cabinet", label: "Cabinet" },
                  { key: "telephone", label: "Téléphone" },
                  { key: "message", label: "Message" },
                  { key: "created_at", label: "Date" },
                ]);
              } else {
                downloadFromApi("/api/admin/export/partners.xlsx", "auprea_partenaires_export.xlsx");
              }
            }}
          />
        )}
      </div>
      {leads.length === 0 ? (
        <div style={{ padding: 20, color: "rgba(35,38,46,0.6)", fontSize: 14 }}>Aucune demande de partenariat pour l'instant.</div>
      ) : (
        <div className="space-y-3" style={{ maxHeight: 360, overflowY: "auto" }}>
          {leads.map((l) => (
            <div key={l.id} style={{ padding: 14, border: "1px solid rgba(21,41,79,0.1)" }} data-testid={`partner-lead-${l.email}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <div>
                  <strong>{l.nom_complet}</strong>
                  <span style={{ color: "rgba(35,38,46,0.6)", fontSize: 13, marginLeft: 8 }}>· {l.profession}</span>
                </div>
                <span style={{ fontSize: 12, color: "rgba(35,38,46,0.5)" }}>
                  {l.created_at ? new Date(l.created_at).toLocaleString("fr-FR") : ""}
                </span>
              </div>
              <div style={{ fontSize: 13, color: "rgba(35,38,46,0.7)" }}>
                {l.cabinet && <span>{l.cabinet} · </span>}
                <a href={`mailto:${l.email}`} style={{ color: "var(--auprea-blue)" }}>{l.email}</a>
                {l.telephone && <span> · {l.telephone}</span>}
              </div>
              {l.message && <div style={{ marginTop: 8, fontSize: 14, fontStyle: "italic", color: "var(--auprea-ink)" }}>« {l.message} »</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SECTION BILANS SUCCESSION — nouvel onglet dédié (iter 7)
// =============================================================================
const DIAG_META = {
  urgence: { label: "Urgence critique", color: "#b53a3a" },
  en_cours: { label: "En cours de sécurisation", color: "#caa86a" },
  serein: { label: "Sérénité totale", color: "#2f7d5b" },
};
const EXPERT_LABELS = {
  notaire: "Notaire",
  agent_immobilier: "Agent immobilier",
  avocat: "Avocat",
  coordinateur_auprea: "Coordinateur AUPRÉA",
};
const RAPPEL_STATUS_LABELS = {
  nouveau: "Nouveau",
  traite: "Traité",
  hors_delai: "Hors délai",
};
const BILAN_QUESTION_LABELS = {
  admin_q1: "Docs d'identité à jour ?",
  admin_q2: "Actes de propriété <5min ?",
  admin_q3: "Inventaire écrit ?",
  admin_q4: "Accès numériques sécurisés ?",
  admin_q5: "Proches savent où trouver ?",
  juridique_q6: "Testament enregistré ?",
  juridique_q7: "Personne de confiance désignée ?",
  juridique_q8: "Mandat de protection future ?",
  juridique_q9: "Droits des héritiers connus ?",
  juridique_q10: "Volontés funéraires exprimées ?",
  financier_q11: "Patrimoine actualisé (12 mois) ?",
  financier_q12: "Clauses bénéf. assurances-vie révisées ?",
  financier_q13: "Donations/démembrement en place ?",
  financier_q14: "Dettes listées ?",
  financier_q15: "Consulté un expert ?",
};
const ANSWER_FR = { oui: "Oui", partiel: "Partiellement", non: "Non", "": "—" };

// Bilan Dirigeants — mappings
const DIRIGEANT_DIAG_META = {
  risque:      { label: "Zone à risque", color: "#b53a3a" },
  vigilance:   { label: "Vigilance", color: "#caa86a" },
  bien_engage: { label: "Bien engagé", color: "#1d3a72" },
  exemplaire:  { label: "Exemplaire", color: "#2f7d5b" },
};
const DIRIGEANT_QUESTION_LABELS = {
  gouv_q1: "Continuité si indisponible plusieurs sem. ?",
  gouv_q2: "Mandat/délégation formalisé ?",
  gouv_q3: "Tiers de confiance connaît les accès ?",
  struct_q4: "Valorisation < 3 ans ?",
  struct_q5: "Statuts prévoient décès/incapacité ?",
  struct_q6: "Clause de rachat assurée ?",
  trans_q7: "Successeur identifié et informé ?",
  trans_q8: "Plan de transmission engagé ?",
  trans_q9: "Dispositifs fiscaux connus (Dutreil) ?",
  prot_q10: "Famille protégée (homme-clé) ?",
  prot_q11: "Patrimoine pro/perso séparé ?",
  antic_q12: "Testament rédigé ?",
  antic_q13: "Docs essentiels accessibles ?",
  antic_q14: "Dernier point stratégique < 1 an ?",
};
const DIRIGEANT_SECTION_LABELS = {
  gouvernance: "Gouvernance & dépendance",
  structure: "Structure & valorisation",
  transmission: "Transmission & fiscalité",
  protection: "Protection famille",
  anticipation: "Anticipation & documents",
};
const DIRIGEANT_ANSWER_FR = { a: "A (idéal)", b: "B (partiel)", c: "C (non)", "": "—" };

function bilanSegment(b) { return b?.segment || "particulier"; }
function bilanMax(b) { return b?.score_max || (bilanSegment(b) === "dirigeant" ? 42 : 30); }
function bilanDiagMeta(b) {
  const seg = bilanSegment(b);
  const meta = seg === "dirigeant" ? DIRIGEANT_DIAG_META : DIAG_META;
  return meta[b.diagnostic] || {};
}

function BilansSuccessionSection() {
  const [data, setData] = useState({ total: 0, items: [], kpi: null });
  const [segmentFilter, setSegmentFilter] = useState(""); // "" | particulier | dirigeant
  const [rappelFilter, setRappelFilter] = useState(""); // "" | "true" | "false"
  const [diagnosticFilter, setDiagnosticFilter] = useState(""); // dynamic based on segment
  const [statusFilter, setStatusFilter] = useState(""); // "" | nouveau | traite | hors_delai
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [detailBilan, setDetailBilan] = useState(null); // ouverture modal
  const [loading, setLoading] = useState(false);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit,
        segment: segmentFilter,
        rappel: rappelFilter,
        diagnostic: diagnosticFilter,
        rappel_status: statusFilter,
        search,
      });
      const { data: r } = await api.get(`/admin/bilans?${params}`);
      setData(r);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  }, [page, segmentFilter, rappelFilter, diagnosticFilter, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Reset diagnostic filter when segment changes (les codes ne se chevauchent pas)
  useEffect(() => { setDiagnosticFilter(""); }, [segmentFilter]);

  const rappelsPending = data.kpi?.rappels_pending || 0;

  return (
    <div className="mb-16" data-testid="admin-bilans-section">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 style={{ fontSize: 22, display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            Bilans
            {rappelsPending > 0 && (
              <span
                style={{
                  background: "#b53a3a",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 999,
                  letterSpacing: "0.05em",
                }}
                data-testid="rappels-pending-badge"
              >
                {rappelsPending} rappel{rappelsPending > 1 ? "s" : ""} en attente
              </span>
            )}
          </h2>
          <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
            Quiz d'auto-évaluation (particuliers + dirigeants) · {data.total} résultat{data.total > 1 ? "s" : ""} filtré{data.total > 1 ? "s" : ""}
            {data.kpi && (
              <> · <strong>{data.kpi.total_particulier || 0}</strong> particulier{(data.kpi.total_particulier || 0) > 1 ? "s" : ""} · <strong>{data.kpi.total_dirigeant || 0}</strong> dirigeant{(data.kpi.total_dirigeant || 0) > 1 ? "s" : ""}</>
            )}
          </p>
        </div>
        <ExportMenu
          testIdPrefix="export-bilans"
          disabled={data.total === 0}
          onExport={(fmt) => {
            const qs = new URLSearchParams({ segment: segmentFilter, rappel: rappelFilter, diagnostic: diagnosticFilter, rappel_status: statusFilter, search }).toString();
            const date = new Date().toISOString().slice(0, 10);
            if (fmt === "csv") {
              api.get(`/admin/bilans?${qs}&limit=5000`).then((r) => {
                const items = r.data?.items || [];
                const rows = items.map((b) => {
                  const seg = bilanSegment(b);
                  const diagLbl = (seg === "dirigeant" ? DIRIGEANT_DIAG_META : DIAG_META)[b.diagnostic]?.label || "";
                  return {
                    segment: seg === "dirigeant" ? "Dirigeant" : "Particulier",
                    created_at: b.created_at ? new Date(b.created_at).toLocaleString("fr-FR") : "",
                    prenom: b.prenom || "", nom: b.nom || "",
                    entreprise: b.entreprise || "",
                    email: b.email || "", telephone: b.telephone || "",
                    score_total: `${b.score_total} / ${bilanMax(b)}`,
                    diagnostic: diagLbl,
                    expert: EXPERT_LABELS[b.expert_prioritaire] || "",
                    rappel_demande: b.rappel_demande ? "Oui" : "Non",
                    rappel_status: RAPPEL_STATUS_LABELS[b.rappel_status] || "",
                  };
                });
                downloadCSV(`auprea_bilans_${date}.csv`, rows, [
                  { key: "segment", label: "Segment" },
                  { key: "created_at", label: "Date" },
                  { key: "prenom", label: "Prénom" }, { key: "nom", label: "Nom" },
                  { key: "entreprise", label: "Entreprise" },
                  { key: "email", label: "Email" }, { key: "telephone", label: "Téléphone" },
                  { key: "score_total", label: "Score" }, { key: "diagnostic", label: "Diagnostic" },
                  { key: "expert", label: "Expert" },
                  { key: "rappel_demande", label: "Rappel demandé" }, { key: "rappel_status", label: "Statut rappel" },
                ]);
              });
            } else {
              downloadFromApi(`/api/admin/export/bilans.xlsx?${qs}`, `auprea_bilans_${date}.xlsx`);
            }
          }}
        />
      </div>

      {/* KPI cards */}
      {data.kpi && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="bilan-kpi">
          <BilanKpiCard label="Bilans total" value={data.kpi.total} testid="kpi-bilans-total" />
          <BilanKpiCard label="Particuliers" value={data.kpi.total_particulier || 0} color="#1d3a72" testid="kpi-bilans-particulier" />
          <BilanKpiCard label="Dirigeants (Pros)" value={data.kpi.total_dirigeant || 0} color="#caa86a" testid="kpi-bilans-dirigeant" />
          <BilanKpiCard label="Urgence / Risque" value={(data.kpi.urgence || 0) + (data.kpi.risque || 0)} color="#b53a3a" testid="kpi-bilans-urgence" />
          <BilanKpiCard label="Score moyen" value={data.kpi.avg_score} testid="kpi-bilans-avg" />
        </div>
      )}

      {/* Onglets rapides Segment */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="bilan-segment-tabs">
        {[
          { v: "", lbl: "Tous", tid: "tab-all" },
          { v: "particulier", lbl: "Bilan Particuliers", tid: "tab-particulier" },
          { v: "dirigeant", lbl: "Bilan Pros", tid: "tab-dirigeant" },
        ].map((t) => {
          const active = segmentFilter === t.v;
          return (
            <button
              key={t.v || "all"}
              type="button"
              onClick={() => { setPage(0); setSegmentFilter(t.v); }}
              data-testid={t.tid}
              style={{
                padding: "8px 16px",
                border: `1px solid ${active ? "var(--auprea-navy)" : "rgba(21,41,79,0.15)"}`,
                background: active ? "var(--auprea-navy)" : "#fff",
                color: active ? "#f3efe6" : "var(--auprea-ink)",
                borderRadius: 999,
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                cursor: "pointer",
                letterSpacing: "0.03em",
              }}
            >
              {t.lbl}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <select
          className="auprea-input" style={{ maxWidth: 220 }}
          value={rappelFilter}
          onChange={(e) => { setPage(0); setRappelFilter(e.target.value); }}
          data-testid="bilan-filter-rappel"
        >
          <option value="">Tous les bilans</option>
          <option value="true">Rappel demandé</option>
          <option value="false">Sans demande de rappel</option>
        </select>
        <select
          className="auprea-input" style={{ maxWidth: 200 }}
          value={diagnosticFilter}
          onChange={(e) => { setPage(0); setDiagnosticFilter(e.target.value); }}
          data-testid="bilan-filter-diagnostic"
        >
          <option value="">Tous diagnostics</option>
          {segmentFilter !== "dirigeant" && (
            <>
              <option value="urgence">Urgence critique</option>
              <option value="en_cours">En cours</option>
              <option value="serein">Sérénité totale</option>
            </>
          )}
          {segmentFilter !== "particulier" && (
            <>
              <option value="risque">Zone à risque</option>
              <option value="vigilance">Vigilance</option>
              <option value="bien_engage">Bien engagé</option>
              <option value="exemplaire">Exemplaire</option>
            </>
          )}
        </select>
        <select
          className="auprea-input" style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}
          data-testid="bilan-filter-status"
        >
          <option value="">Tous statuts</option>
          <option value="nouveau">Nouveau</option>
          <option value="traite">Traité</option>
          <option value="hors_delai">Hors délai</option>
        </select>
        <input
          className="auprea-input"
          style={{ minWidth: 220, flex: 1 }}
          placeholder="Rechercher (nom, prénom, email, entreprise)"
          value={search}
          onChange={(e) => { setPage(0); setSearch(e.target.value); }}
          data-testid="bilan-search"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="bilan-table">
          <thead>
            <tr style={{ borderBottom: "2px solid rgba(21,41,79,0.15)" }}>
              {["Segment", "Date", "Prénom & Nom", "Entreprise", "Email", "Score", "Diagnostic", "Rappel", "Statut", ""].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "10px 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={10} style={{ padding: "30px 8px", textAlign: "center", color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>Aucun bilan pour ces critères.</td></tr>
            )}
            {data.items.map((b) => {
              const seg = bilanSegment(b);
              const diag = bilanDiagMeta(b);
              const max = bilanMax(b);
              return (
                <tr key={b.id} style={{ borderBottom: "1px solid rgba(21,41,79,0.06)" }} data-testid={`bilan-row-${b.id}`}>
                  <td style={{ padding: "12px 8px" }}>
                    <span
                      data-testid={`bilan-segment-${b.id}`}
                      style={{
                        background: seg === "dirigeant" ? "var(--auprea-gold)" : "rgba(21,41,79,0.08)",
                        color: seg === "dirigeant" ? "var(--auprea-navy)" : "var(--auprea-navy)",
                        padding: "3px 10px",
                        borderRadius: 3,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {seg === "dirigeant" ? "Pro" : "Part."}
                    </span>
                  </td>
                  <td style={{ padding: "12px 8px", fontSize: 13, color: "rgba(35,38,46,0.7)", whiteSpace: "nowrap" }}>{b.created_at ? new Date(b.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : ""}</td>
                  <td style={{ padding: "12px 8px", fontSize: 14 }}><span style={{ fontWeight: 600, color: "var(--auprea-navy)" }}>{b.prenom} {b.nom}</span></td>
                  <td style={{ padding: "12px 8px", fontSize: 13, color: "rgba(35,38,46,0.75)" }}>{b.entreprise || (seg === "dirigeant" ? "—" : "")}</td>
                  <td style={{ padding: "12px 8px", fontSize: 13 }}><a href={`mailto:${b.email}`} style={{ color: "var(--auprea-blue)" }}>{b.email}</a></td>
                  <td style={{ padding: "12px 8px", fontSize: 14, fontWeight: 600, color: "var(--auprea-navy)" }}>{b.score_total}<span style={{ color: "rgba(35,38,46,0.4)", fontWeight: 400 }}> /{max}</span></td>
                  <td style={{ padding: "12px 8px" }}><span style={{ background: diag.color, color: "#fff", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em" }}>{diag.label}</span></td>
                  <td style={{ padding: "12px 8px" }}>
                    {b.rappel_demande ? <span style={{ background: "#b53a3a", color: "#fff", padding: "3px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>OUI</span> : <span style={{ color: "rgba(35,38,46,0.35)", fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: "12px 8px", fontSize: 12 }}>{b.rappel_demande ? (RAPPEL_STATUS_LABELS[b.rappel_status] || "—") : ""}</td>
                  <td style={{ padding: "12px 8px" }}>
                    <button onClick={() => setDetailBilan(b)} style={{ fontSize: 13, color: "var(--auprea-blue)", textDecoration: "underline" }} data-testid={`btn-detail-${b.id}`}>Détail</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.total > limit && (
        <div className="flex items-center justify-between mt-4" style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
          <span>Page {page + 1} · {data.total} bilan{data.total > 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="auprea-btn-outline" style={{ padding: "6px 12px", opacity: page === 0 ? 0.4 : 1 }}>‹ Précédent</button>
            <button disabled={(page + 1) * limit >= data.total} onClick={() => setPage(page + 1)} className="auprea-btn-outline" style={{ padding: "6px 12px", opacity: (page + 1) * limit >= data.total ? 0.4 : 1 }}>Suivant ›</button>
          </div>
        </div>
      )}

      {detailBilan && <BilanDetailModal bilan={detailBilan} onClose={() => setDetailBilan(null)} onUpdated={load} />}
    </div>
  );
}

function SuccessionsSection() {
  const [data, setData] = useState({ total: 0, items: [], kpi: null });
  const [paidFilter, setPaidFilter] = useState("");
  const [territoireFilter, setTerritoireFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: page * limit, limit,
        paid: paidFilter, territoire: territoireFilter, tag: tagFilter, search,
      });
      const { data: r } = await api.get(`/admin/successions?${params}`);
      setData(r);
    } catch (_e) { /* silent */ }
  }, [page, paidFilter, territoireFilter, tagFilter, search]);
  useEffect(() => { load(); }, [load]);

  const kpi = data.kpi || {};
  const metierMeta = data.professions_meta || {};
  const tagMeta = data.tag_meta || {};
  const topMetiers = Object.entries(kpi.metier_counter || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const topTerritoires = Object.entries(kpi.territoire_counter || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="mb-16" data-testid="admin-successions-section">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 style={{ fontSize: 22, color: "var(--auprea-navy)", display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: "#1F5E56", color: "#EFF1EC", padding: "2px 10px", borderRadius: 3, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700 }}>Module dédié</span>
            Ouvrir une succession
          </h2>
          <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
            Parcours diagnostic + feuille de route payante 89€ · {kpi.total || 0} dossier{(kpi.total || 0) > 1 ? "s" : ""} réel{(kpi.total || 0) > 1 ? "s" : ""} · {kpi.paid || 0} payé{(kpi.paid || 0) > 1 ? "s" : ""}
            {kpi.demo_paid ? <span style={{ marginLeft: 8, background: "rgba(21,41,79,0.08)", color: "#15294f", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600 }}>+ {kpi.demo_paid} démo interne{kpi.demo_paid > 1 ? "s" : ""} (exclue{kpi.demo_paid > 1 ? "s" : ""} des KPI)</span> : null}
          </p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="succ-kpi">
        <BilanKpiCard label="Dossiers ouverts (réels)" value={kpi.total || 0} color="#1F5E56" testid="kpi-succ-total" />
        <BilanKpiCard label="CA feuilles de route" value={`${(kpi.revenue_eur || 0).toLocaleString("fr-FR")} €`} color="#AD7C33" testid="kpi-succ-revenue" />
        <BilanKpiCard label="Taux de conversion" value={`${kpi.paid_rate_pct || 0}%`} color="#1d3a72" testid="kpi-succ-conv" />
        <BilanKpiCard label="Climat conflictuel" value={`${kpi.conflit_pct || 0}%`} color="#b53a3a" testid="kpi-succ-conflit" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <select className="auprea-input" style={{ maxWidth: 200 }} value={paidFilter} onChange={(e) => { setPage(0); setPaidFilter(e.target.value); }} data-testid="succ-filter-paid">
          <option value="">Tous</option>
          <option value="true">Payés</option>
          <option value="false">Diagnostic gratuit seul</option>
        </select>
        <select className="auprea-input" style={{ maxWidth: 200 }} value={territoireFilter} onChange={(e) => { setPage(0); setTerritoireFilter(e.target.value); }} data-testid="succ-filter-territoire">
          <option value="">Tous territoires</option>
          {["Guadeloupe", "Martinique", "Guyane", "La Réunion", "Mayotte", "Hexagone", "Étranger"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="auprea-input" style={{ maxWidth: 220 }} value={tagFilter} onChange={(e) => { setPage(0); setTagFilter(e.target.value); }} data-testid="succ-filter-tag">
          <option value="">Toutes situations</option>
          {Object.keys(tagMeta).map((k) => <option key={k} value={k}>{tagMeta[k]?.label || k}</option>)}
        </select>
        <input className="auprea-input" style={{ minWidth: 220, flex: 1 }} placeholder="Rechercher (nom, prénom, ville)" value={search} onChange={(e) => { setPage(0); setSearch(e.target.value); }} data-testid="succ-search" />
      </div>

      {/* Two mini charts : top métiers & top territoires */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div style={{ background: "#fff", padding: 16, border: "1px solid rgba(31,94,86,0.15)", borderRadius: 4 }} data-testid="succ-top-metiers">
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", marginBottom: 8 }}>Métiers les plus sollicités</div>
          {topMetiers.length === 0 ? <div style={{ fontSize: 13, color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>Aucune donnée.</div> : topMetiers.map(([k, v]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13 }}>
              <span style={{ flex: 1 }}>{metierMeta[k]?.icon} {metierMeta[k]?.label || k}</span>
              <div style={{ background: "rgba(31,94,86,0.1)", height: 6, borderRadius: 3, width: 140 }}><div style={{ background: "#1F5E56", height: "100%", width: `${Math.min(100, v * 8)}%`, borderRadius: 3 }} /></div>
              <span style={{ fontWeight: 600, color: "#1F5E56", minWidth: 30, textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ background: "#fff", padding: 16, border: "1px solid rgba(31,94,86,0.15)", borderRadius: 4 }} data-testid="succ-top-territoires">
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", marginBottom: 8 }}>Répartition géographique</div>
          {topTerritoires.length === 0 ? <div style={{ fontSize: 13, color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>Aucune donnée.</div> : topTerritoires.map(([k, v]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13 }}>
              <span style={{ flex: 1 }}>{k}</span>
              <div style={{ background: "rgba(173,124,51,0.15)", height: 6, borderRadius: 3, width: 140 }}><div style={{ background: "#AD7C33", height: "100%", width: `${Math.min(100, v * 8)}%`, borderRadius: 3 }} /></div>
              <span style={{ fontWeight: 600, color: "#AD7C33", minWidth: 30, textAlign: "right" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="succ-table">
          <thead>
            <tr style={{ borderBottom: "2px solid rgba(31,94,86,0.2)" }}>
              {["Date", "Client", "Défunt", "Territoire / Ville", "Situations", "Métiers", "Statut"].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "10px 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={7} style={{ padding: "24px 8px", textAlign: "center", color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>Aucun dossier pour ces critères.</td></tr>}
            {data.items.map((it) => (
              <tr key={it.id} style={{ borderBottom: "1px solid rgba(31,94,86,0.08)" }} data-testid={`succ-row-${it.id}`}>
                <td style={{ padding: "10px 8px", fontSize: 12, color: "rgba(35,38,46,0.7)", whiteSpace: "nowrap" }}>{it.created_at ? new Date(it.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) : ""}</td>
                <td style={{ padding: "10px 8px", fontSize: 13 }}>{it.user_email}</td>
                <td style={{ padding: "10px 8px", fontSize: 13, fontWeight: 600, color: "#123B36" }}>{(it.situation?.defunt_prenom || "") + " " + (it.situation?.defunt_nom || "")}</td>
                <td style={{ padding: "10px 8px", fontSize: 12, color: "rgba(35,38,46,0.7)" }}>{it.situation?.territoire || "—"}{it.situation?.ville ? ` · ${it.situation.ville}` : ""}</td>
                <td style={{ padding: "10px 8px", fontSize: 11 }}>
                  <div className="flex flex-wrap gap-1">
                    {(it.tags || []).slice(0, 3).map((t) => (
                      <span key={t} style={{ background: tagMeta[t]?.color || "#1F5E56", color: "#fff", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>{tagMeta[t]?.label || t}</span>
                    ))}
                  </div>
                </td>
                <td style={{ padding: "10px 8px", fontSize: 12, color: "rgba(35,38,46,0.7)" }}>{(it.professions || []).map((p) => metierMeta[p]?.icon || "").join(" ") + " " + (it.professions || []).length + " métier(s)"}</td>
                <td style={{ padding: "10px 8px" }}>
                  {it.paid
                    ? (it.is_demo
                        ? <span style={{ background: "#15294f", color: "#fff", padding: "3px 10px", borderRadius: 3, fontSize: 10, fontWeight: 700 }} data-testid={`succ-demo-badge-${it.id}`}>DÉMO INTERNE · 0€</span>
                        : <span style={{ background: "#AD7C33", color: "#fff", padding: "3px 10px", borderRadius: 3, fontSize: 10, fontWeight: 700 }}>PAYÉ · 89€</span>)
                    : <span style={{ color: "rgba(35,38,46,0.4)", fontSize: 11, fontStyle: "italic" }}>Gratuit</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.total > limit && (
        <div className="flex items-center justify-between mt-4" style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>
          <span>Page {page + 1} · {data.total} dossier{data.total > 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="auprea-btn-outline" style={{ padding: "6px 12px", opacity: page === 0 ? 0.4 : 1 }}>‹ Précédent</button>
            <button disabled={(page + 1) * limit >= data.total} onClick={() => setPage(page + 1)} className="auprea-btn-outline" style={{ padding: "6px 12px", opacity: (page + 1) * limit >= data.total ? 0.4 : 1 }}>Suivant ›</button>
          </div>
        </div>
      )}
    </div>
  );
}


function BilanKpiCard({ label, value, color = "var(--auprea-navy)", testid }) {
  return (
    <div style={{ padding: "16px 18px", background: "#fff", border: "1px solid rgba(21,41,79,0.08)", borderTop: `3px solid ${color}`, borderRadius: 4 }} data-testid={testid}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 26, fontWeight: 500, color }}>{value}</div>
    </div>
  );
}

function BilanDetailModal({ bilan, onClose, onUpdated }) {
  const [status, setStatus] = useState(bilan.rappel_status || "nouveau");
  const [notes, setNotes] = useState(bilan.rappel_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/bilans/${bilan.id}`, { rappel_status: status, rappel_notes: notes });
      onUpdated && onUpdated();
      onClose();
    } catch (_e) { /* silent */ }
    finally { setSaving(false); }
  };

  const seg = bilanSegment(bilan);
  const diag = bilanDiagMeta(bilan);
  const max = bilanMax(bilan);
  const isDirigeant = seg === "dirigeant";
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}
      data-testid="bilan-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", maxWidth: 720, width: "100%", maxHeight: "90vh", overflow: "auto", borderRadius: 6, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
      >
        <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "inline-block", background: isDirigeant ? "var(--auprea-gold)" : "rgba(243,239,230,0.15)", color: isDirigeant ? "var(--auprea-navy)" : "#f3efe6", padding: "3px 10px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
              {isDirigeant ? "Bilan Pros" : "Bilan Particuliers"}
            </div>
            <div style={{ fontFamily: "Spectral, serif", fontSize: 22 }}>{bilan.prenom} {bilan.nom}</div>
            {isDirigeant && bilan.entreprise && (
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>{bilan.entreprise}{bilan.secteur_activite ? ` · ${bilan.secteur_activite}` : ""}</div>
            )}
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              {bilan.email} · {bilan.telephone || "pas de téléphone"}
              {!isDirigeant && bilan.annee_naissance ? ` · Né(e) en ${bilan.annee_naissance}` : ""}
            </div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>{new Date(bilan.created_at).toLocaleString("fr-FR")}</div>
          </div>
          <button onClick={onClose} style={{ color: "#f3efe6", fontSize: 22, background: "none", border: "none", cursor: "pointer" }} data-testid="btn-detail-close">×</button>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontFamily: "Spectral, serif", fontSize: 32, color: "var(--auprea-navy)" }}>{bilan.score_total}<span style={{ fontSize: 18, color: "rgba(35,38,46,0.4)" }}> /{max}</span></div>
            <span style={{ background: diag.color, color: "#fff", padding: "4px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>{diag.label}</span>
            {!isDirigeant && (
              <span style={{ fontSize: 13, color: "rgba(35,38,46,0.6)" }}>Expert : <strong style={{ color: "var(--auprea-navy)" }}>{EXPERT_LABELS[bilan.expert_prioritaire]}</strong></span>
            )}
          </div>

          {isDirigeant ? (
            <>
              {/* Détail sections dirigeant */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" style={{ fontSize: 12 }}>
                {Object.entries(bilan.score_sections || {}).map(([k, v]) => (
                  <div key={k}>
                    <div style={{ color: "rgba(35,38,46,0.5)", fontSize: 10, textTransform: "uppercase", marginBottom: 2 }}>{DIRIGEANT_SECTION_LABELS[k] || k}</div>
                    <strong>{v.score} / {v.max}</strong>
                  </div>
                ))}
              </div>
              {Array.isArray(bilan.weak_sections) && bilan.weak_sections.length > 0 && (
                <div style={{ background: "rgba(202,168,106,0.1)", border: "1px solid rgba(202,168,106,0.3)", padding: "10px 14px", borderRadius: 4, marginBottom: 20, fontSize: 13 }} data-testid="detail-weak-sections">
                  <strong style={{ color: "var(--auprea-navy)" }}>Priorités à traiter :</strong>{" "}
                  {bilan.weak_sections.map((k) => DIRIGEANT_SECTION_LABELS[k] || k).join(" · ")}
                </div>
              )}
              <details style={{ marginBottom: 20 }}>
                <summary style={{ cursor: "pointer", fontSize: 14, fontWeight: 600, color: "var(--auprea-navy)", padding: "6px 0" }}>Voir les 14 réponses détaillées</summary>
                <div style={{ marginTop: 12, fontSize: 13 }}>
                  {Object.entries(DIRIGEANT_QUESTION_LABELS).map(([k, lbl]) => {
                    const ans = bilan.reponses?.[k] || "";
                    const color = ans === "a" ? "var(--auprea-green)" : ans === "c" ? "#b53a3a" : ans === "b" ? "#caa86a" : "rgba(35,38,46,0.35)";
                    return (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(21,41,79,0.06)" }}>
                        <span style={{ color: "rgba(35,38,46,0.75)" }}>{lbl}</span>
                        <span style={{ color, fontWeight: 600, whiteSpace: "nowrap", marginLeft: 12 }}>{DIRIGEANT_ANSWER_FR[ans]}</span>
                      </div>
                    );
                  })}
                </div>
              </details>
            </>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-6" style={{ fontSize: 13 }}>
                <div><div style={{ color: "rgba(35,38,46,0.5)", fontSize: 11, textTransform: "uppercase", marginBottom: 2 }}>Administratif</div><strong>{bilan.score_admin} / 10</strong></div>
                <div><div style={{ color: "rgba(35,38,46,0.5)", fontSize: 11, textTransform: "uppercase", marginBottom: 2 }}>Juridique</div><strong>{bilan.score_juridique} / 10</strong></div>
                <div><div style={{ color: "rgba(35,38,46,0.5)", fontSize: 11, textTransform: "uppercase", marginBottom: 2 }}>Financier</div><strong>{bilan.score_financier} / 10</strong></div>
              </div>

              <details style={{ marginBottom: 20 }}>
                <summary style={{ cursor: "pointer", fontSize: 14, fontWeight: 600, color: "var(--auprea-navy)", padding: "6px 0" }}>Voir les 15 réponses détaillées</summary>
                <div style={{ marginTop: 12, fontSize: 13 }}>
                  {Object.entries(BILAN_QUESTION_LABELS).map(([k, lbl]) => {
                    const ans = bilan.reponses?.[k] || "";
                    const color = ans === "oui" ? "var(--auprea-green)" : ans === "non" ? "#b53a3a" : ans === "partiel" ? "#caa86a" : "rgba(35,38,46,0.35)";
                    return (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(21,41,79,0.06)" }}>
                        <span style={{ color: "rgba(35,38,46,0.75)" }}>{lbl}</span>
                        <span style={{ color, fontWeight: 600, whiteSpace: "nowrap", marginLeft: 12 }}>{ANSWER_FR[ans]}</span>
                      </div>
                    );
                  })}
                </div>
              </details>
            </>
          )}

          {bilan.rappel_demande && (
            <div style={{ background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.3)", padding: 16, borderRadius: 4, marginBottom: 16 }} data-testid="rappel-panel">
              <div style={{ fontSize: 13, fontWeight: 600, color: "#b53a3a", marginBottom: 10 }}>🔔 Demande de rappel — {bilan.rappel_demande_at ? new Date(bilan.rappel_demande_at).toLocaleString("fr-FR") : ""}</div>
              <label style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", fontWeight: 600 }}>Statut</label>
              <select className="auprea-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="bilan-status-select" style={{ marginTop: 4, marginBottom: 12 }}>
                <option value="nouveau">Nouveau</option>
                <option value="traite">Traité</option>
                <option value="hors_delai">Hors délai</option>
              </select>
              <label style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(35,38,46,0.6)", fontWeight: 600 }}>Notes internes</label>
              <textarea className="auprea-input" style={{ marginTop: 4, minHeight: 80 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Compte-rendu de l'appel, actions à venir..." data-testid="bilan-notes-input" />
              <button onClick={save} disabled={saving} className="auprea-btn" style={{ marginTop: 12, padding: "10px 20px", minHeight: "auto", fontSize: 13 }} data-testid="btn-save-bilan">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <a href={`mailto:${bilan.email}?subject=${encodeURIComponent("Votre bilan succession AUPRÉA")}`} className="auprea-btn-outline" style={{ padding: "8px 16px", minHeight: "auto", fontSize: 13, textDecoration: "none" }} data-testid="btn-email-client">
              <Mail size={14} style={{ marginRight: 6, display: "inline-block", verticalAlign: "-2px" }} /> Envoyer un email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}



// =============================================================================
// ManagePlanModal — Changer manuellement le forfait d'un client + Historique
// =============================================================================
const PLAN_OPTIONS = [
  { id: "decouverte", label: "Découverte (gratuit)" },
  { id: "essentiel", label: "Essentiel — 9,99 €/mois" },
  { id: "confort", label: "Confort — 19,90 €/mois" },
  { id: "patrimoine_plus", label: "Patrimoine+ — 29,99 €/mois" },
];

function ManagePlanModal({ u, onClose, onUpdated }) {
  const [tab, setTab] = useState("change");
  const [newPlan, setNewPlan] = useState(u.plan_id || "decouverte");
  const [reason, setReason] = useState("");
  const [expiration, setExpiration] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (tab === "history") {
      setLoadingHistory(true);
      api.get(`/admin/users/${u.id}/plan-history`)
        .then((r) => setHistory(r.data?.items || []))
        .catch(() => setHistory([]))
        .finally(() => setLoadingHistory(false));
    }
  }, [tab, u.id]);

  const save = async () => {
    if (!reason.trim()) { setError("Une raison est obligatoire."); return; }
    if (newPlan === u.plan_id) { setError("Ce client est déjà sur ce forfait."); return; }
    setError(""); setSaving(true);
    try {
      await api.post(`/admin/users/${u.id}/plan`, {
        plan_id: newPlan,
        reason: reason.trim(),
        manual_override: true,
        expiration: expiration ? new Date(expiration).toISOString() : null,
      });
      onUpdated();
    } catch (e) {
      setError(e?.response?.data?.detail || "Échec de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  const planLabel = (p) => PLAN_OPTIONS.find((o) => o.id === p)?.label.split(" —")[0] || p;
  const methodLabel = (m) => ({
    stripe_webhook: "Stripe (webhook)",
    manual_admin: "Manuel admin",
    stripe_api: "Stripe API",
    referral_reward: "Récompense parrainage",
    stripe_cancel: "Annulation Stripe",
  }[m] || m);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(21,41,79,0.55)", zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      data-testid="manage-plan-modal"
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", maxWidth: 620, width: "100%", maxHeight: "90vh", overflow: "auto", borderRadius: 6, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "Spectral, serif", fontSize: 20, marginBottom: 4 }}>{u.full_name || u.email}</div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>{u.email} · Forfait actuel : <strong style={{ color: "var(--auprea-gold)" }}>{planLabel(u.plan_id)}</strong>{u.plan_manual_override && <span style={{ marginLeft: 8, fontSize: 11, background: "#c9a227", color: "#16223f", padding: "2px 8px", borderRadius: 3, fontWeight: 700 }}>OVERRIDE MANUEL</span>}</div>
          </div>
          <button onClick={onClose} style={{ color: "#f3efe6", background: "none", border: "none", cursor: "pointer", fontSize: 22 }} data-testid="manage-plan-close">×</button>
        </div>
        <div style={{ borderBottom: "1px solid rgba(21,41,79,0.1)", padding: "0 24px", display: "flex", gap: 12 }}>
          <button onClick={() => setTab("change")} style={{ padding: "12px 4px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: tab === "change" ? 600 : 500, color: tab === "change" ? "var(--auprea-navy)" : "rgba(35,38,46,0.55)", borderBottom: tab === "change" ? "2px solid var(--auprea-gold)" : "2px solid transparent" }} data-testid="tab-change-plan">Changer le forfait</button>
          <button onClick={() => setTab("history")} style={{ padding: "12px 4px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: tab === "history" ? 600 : 500, color: tab === "history" ? "var(--auprea-navy)" : "rgba(35,38,46,0.55)", borderBottom: tab === "history" ? "2px solid var(--auprea-gold)" : "2px solid transparent" }} data-testid="tab-history">Historique</button>
        </div>

        <div style={{ padding: 24 }}>
          {tab === "change" && (
            <>
              <div style={{ background: "rgba(202,168,106,0.1)", border: "1px solid rgba(202,168,106,0.4)", padding: "10px 14px", borderRadius: 4, fontSize: 12, color: "#8a6f3d", lineHeight: 1.5, marginBottom: 16 }}>
                <strong>Forçage manuel local</strong> — Cette action ne modifie pas Stripe. Elle sera écrasée si un événement Stripe légitime arrive ensuite pour ce compte (paiement réel).
              </div>
              <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>Nouveau forfait *</label>
              <select className="auprea-input" value={newPlan} onChange={(e) => setNewPlan(e.target.value)} style={{ width: "100%", marginBottom: 12 }} data-testid="select-new-plan">
                {PLAN_OPTIONS.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
              </select>
              <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>Raison (obligatoire) *</label>
              <textarea className="auprea-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. Client a payé Patrimoine+ avec code promo, webhook n'a pas mis à jour son compte." style={{ width: "100%", minHeight: 80, marginBottom: 12 }} data-testid="input-reason" />
              <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>Date d'expiration (facultatif)</label>
              <input type="date" className="auprea-input" value={expiration} onChange={(e) => setExpiration(e.target.value)} style={{ width: "100%", marginBottom: 12 }} data-testid="input-expiration" />

              {error && <div style={{ padding: "10px 14px", background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.3)", color: "#b53a3a", fontSize: 13, borderRadius: 3, marginBottom: 12 }} data-testid="manage-plan-error">{error}</div>}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
                <button onClick={onClose} className="auprea-btn-outline" style={{ padding: "10px 20px", minHeight: "auto", fontSize: 14 }}>Annuler</button>
                <button onClick={save} disabled={saving || !reason.trim()} className="auprea-btn" style={{ padding: "10px 24px", minHeight: "auto", fontSize: 14, opacity: saving || !reason.trim() ? 0.5 : 1 }} data-testid="btn-save-plan">
                  {saving ? "Application…" : "Appliquer le changement"}
                </button>
              </div>
              <p style={{ marginTop: 14, fontSize: 11, color: "rgba(35,38,46,0.55)", lineHeight: 1.5 }}>
                Un email de notification sera automatiquement envoyé au client. Le changement est journalisé dans l'historique.
              </p>
            </>
          )}
          {tab === "history" && (
            <div data-testid="plan-history-list">
              {loadingHistory && <div style={{ color: "rgba(35,38,46,0.5)", fontStyle: "italic" }}>Chargement…</div>}
              {!loadingHistory && history.length === 0 && (
                <div style={{ padding: "20px 0", textAlign: "center", color: "rgba(35,38,46,0.5)", fontStyle: "italic", fontSize: 14 }}>
                  Aucun changement de forfait enregistré pour ce client.
                </div>
              )}
              {history.map((h) => (
                <div key={h.id} style={{ padding: "12px 14px", borderLeft: "3px solid var(--auprea-gold)", background: "rgba(251,246,233,0.5)", marginBottom: 10, fontSize: 13, lineHeight: 1.55 }}>
                  <div style={{ fontSize: 11, color: "rgba(35,38,46,0.6)", marginBottom: 4 }}>
                    {new Date(h.created_at).toLocaleString("fr-FR")} · par <strong>{h.changed_by}</strong> · {methodLabel(h.method)}
                  </div>
                  <div style={{ color: "var(--auprea-navy)", marginBottom: 4 }}>
                    <strong>{planLabel(h.old_plan)}</strong> → <strong style={{ color: "var(--auprea-gold)" }}>{planLabel(h.new_plan)}</strong>
                  </div>
                  {h.reason && <div style={{ color: "rgba(35,38,46,0.75)", fontStyle: "italic", fontSize: 12 }}>« {h.reason} »</div>}
                  {h.expiration && <div style={{ color: "rgba(35,38,46,0.6)", fontSize: 11, marginTop: 3 }}>Expire le : {new Date(h.expiration).toLocaleDateString("fr-FR")}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Section : Sauvegardes secondaires OVH (backup admin panel)
// ============================================================================
function BackupsSection() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [remote, setRemote] = useState({ daily: [], monthly: [], counts: { daily: 0, monthly: 0 } });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [downloadInfo, setDownloadInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, hist, rem] = await Promise.all([
        api.get("/admin/backup/config"),
        api.get("/admin/backup/history"),
        api.get("/admin/backup/list-remote"),
      ]);
      setConfig(cfg.data);
      setHistory(hist.data.items || []);
      setRemote(rem.data || { daily: [], monthly: [], counts: { daily: 0, monthly: 0 } });
    } catch (e) {
      setConfig({ error: "Impossible de charger la configuration" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    if (!window.confirm("Lancer une sauvegarde manuelle maintenant ? (30 secondes environ)")) return;
    setRunning(true);
    setRunResult(null);
    try {
      const r = await api.post("/admin/backup/run");
      setRunResult(r.data);
      await load();
    } catch (e) {
      setRunResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally { setRunning(false); }
  };

  const getDownloadUrl = async (key) => {
    setDownloadInfo({ loading: true, key });
    try {
      const r = await api.get("/admin/backup/download-url", { params: { key } });
      setDownloadInfo({ key, url: r.data.url, expires_in: r.data.expires_in });
    } catch (e) {
      setDownloadInfo({ key, error: e?.response?.data?.detail || e.message });
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} Go`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
    catch (_e) { return iso; }
  };

  if (loading) return <div className="auprea-card" data-testid="backups-loading" style={{ marginBottom: 24 }}>Chargement des sauvegardes…</div>;

  const connOk = config?.connection_test?.ok;
  return (
    <div className="auprea-card" data-testid="admin-backups-section" style={{ marginBottom: 24 }}>
      <div className="flex items-center gap-3 mb-2">
        <Database size={22} style={{ color: "var(--auprea-gold)" }} />
        <h2 style={{ fontSize: 22, margin: 0 }}>Sauvegardes secondaires OVH</h2>
      </div>
      <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", marginBottom: 16 }}>
        Sauvegarde quotidienne automatique (base MongoDB + coffre-fort) vers un bucket OVH indépendant. Chiffrement AES-256 côté client.
      </p>

      {/* Bandeau statut connexion */}
      <div
        data-testid="backup-conn-status"
        style={{
          padding: "12px 16px", marginBottom: 16,
          background: connOk ? "rgba(47,125,91,0.08)" : "rgba(163,59,26,0.08)",
          border: `1px solid ${connOk ? "rgba(47,125,91,0.3)" : "rgba(163,59,26,0.3)"}`,
          borderLeft: `3px solid ${connOk ? "var(--auprea-green)" : "#a33b1a"}`,
          display: "flex", alignItems: "center", gap: 10,
        }}
      >
        {connOk ? <CheckCircle2 size={18} color="var(--auprea-green)" /> : <XCircle size={18} color="#a33b1a" />}
        <div style={{ fontSize: 14, flex: 1 }}>
          <strong>Connexion OVH : {connOk ? "OK" : "ÉCHEC"}</strong>
          <span style={{ color: "rgba(35,38,46,0.65)", marginLeft: 8, fontSize: 12 }}>
            {config?.s3?.bucket} · {config?.s3?.region} · {config?.s3?.endpoint}
          </span>
          {!connOk && config?.connection_test?.error && (
            <div style={{ fontSize: 12, color: "#a33b1a", marginTop: 4 }}>{config.connection_test.error}</div>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <MiniKpi icon={<Calendar />} label="Sauvegardes quotidiennes" value={remote.counts.daily} testid="bk-count-daily" />
        <MiniKpi icon={<HardDrive />} label="Sauvegardes mensuelles" value={remote.counts.monthly} testid="bk-count-monthly" />
        <MiniKpi icon={<Activity />} label="Historique local" value={history.length} testid="bk-history-count" />
        <MiniKpi icon={<Sparkles />} label="Rétention quotidien" value={`${config?.retention_daily_days ?? "-"} j`} testid="bk-retention" />
      </div>

      {/* Ligne "Test de restauration trimestriel" */}
      <RestoreTestBanner config={config} onReload={load} />

      {/* Actions */}
      <div className="flex gap-2 flex-wrap mb-5">
        <button
          onClick={runNow}
          disabled={running || !connOk}
          data-testid="btn-backup-run"
          className="auprea-btn"
          style={{ padding: "9px 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 6, opacity: running || !connOk ? 0.5 : 1 }}
        >
          <Play size={14} /> {running ? "Sauvegarde en cours…" : "Lancer une sauvegarde maintenant"}
        </button>
        <button
          onClick={load}
          disabled={loading}
          data-testid="btn-backup-reload"
          style={{ padding: "9px 16px", fontSize: 13, background: "transparent", color: "var(--auprea-navy)", border: "1px solid var(--auprea-navy)", display: "flex", alignItems: "center", gap: 6 }}
        >
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* Résultat du run manuel */}
      {runResult && (
        <div
          data-testid="backup-run-result"
          style={{
            padding: "12px 16px", marginBottom: 16,
            background: runResult.ok ? "rgba(47,125,91,0.06)" : "rgba(163,59,26,0.08)",
            border: `1px solid ${runResult.ok ? "rgba(47,125,91,0.3)" : "rgba(163,59,26,0.3)"}`,
            borderLeft: `3px solid ${runResult.ok ? "var(--auprea-green)" : "#a33b1a"}`,
            fontSize: 13,
          }}
        >
          {runResult.ok ? (
            <>
              <strong>✅ Sauvegarde réussie</strong> — {runResult.summary?.s3_key} ({fmtSize(runResult.summary?.size_bytes)}, {runResult.summary?.duration_sec}s)
            </>
          ) : (
            <>
              <strong>❌ Échec :</strong> {runResult.error}
            </>
          )}
        </div>
      )}

      {/* Journal des exécutions */}
      <h3 style={{ fontSize: 16, marginBottom: 10, color: "var(--auprea-navy)" }}>Journal des exécutions</h3>
      {history.length === 0 ? (
        <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", fontStyle: "italic" }}>Aucune exécution enregistrée pour l&apos;instant.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }} data-testid="backup-history-table">
            <thead>
              <tr style={{ background: "rgba(21,41,79,0.05)", textAlign: "left" }}>
                <Th>Date</Th>
                <Th>Statut</Th>
                <Th>Origine</Th>
                <Th>Durée</Th>
                <Th>Taille</Th>
                <Th>Clé S3</Th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 20).map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid rgba(35,38,46,0.08)" }} data-testid={`backup-row-${r.id}`}>
                  <Td>{fmtDate(r.started_at)}</Td>
                  <Td>
                    {r.status === "success" ? (
                      <span style={{ color: "var(--auprea-green)", fontWeight: 600 }}>✓ Succès</span>
                    ) : r.status === "failed" ? (
                      <span style={{ color: "#a33b1a", fontWeight: 600 }} title={r.error}>✗ Échec</span>
                    ) : (
                      <span style={{ color: "rgba(35,38,46,0.6)" }}>{r.status}</span>
                    )}
                  </Td>
                  <Td style={{ fontSize: 12 }}>{r.triggered_by}</Td>
                  <Td>{r.duration_sec ? `${r.duration_sec}s` : "-"}</Td>
                  <Td>{fmtSize(r.size_bytes)}</Td>
                  <Td style={{ fontSize: 11, fontFamily: "monospace", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.s3_key || "-"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Liste des archives sur OVH */}
      <h3 style={{ fontSize: 16, marginTop: 24, marginBottom: 10, color: "var(--auprea-navy)" }}>
        Archives présentes sur OVH ({remote.counts.daily + remote.counts.monthly})
      </h3>
      {remote.counts.daily + remote.counts.monthly === 0 ? (
        <p style={{ fontSize: 13, color: "rgba(35,38,46,0.6)", fontStyle: "italic" }}>Aucune archive présente sur le bucket.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }} data-testid="backup-remote-table">
            <thead>
              <tr style={{ background: "rgba(21,41,79,0.05)", textAlign: "left" }}>
                <Th>Type</Th>
                <Th>Nom</Th>
                <Th>Uploadé le</Th>
                <Th>Taille</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {[...remote.daily.map((o) => ({ ...o, kind: "daily" })), ...remote.monthly.map((o) => ({ ...o, kind: "monthly" }))].map((o) => (
                <tr key={o.key} style={{ borderTop: "1px solid rgba(35,38,46,0.08)" }} data-testid={`backup-remote-${o.key}`}>
                  <Td>
                    <span style={{ padding: "2px 8px", fontSize: 11, fontWeight: 600, background: o.kind === "monthly" ? "rgba(202,168,106,0.2)" : "rgba(21,41,79,0.08)", color: o.kind === "monthly" ? "#8a6f2e" : "var(--auprea-navy)" }}>
                      {o.kind === "monthly" ? "MENSUEL" : "QUOTIDIEN"}
                    </span>
                  </Td>
                  <Td style={{ fontFamily: "monospace", fontSize: 11 }}>{o.key.split("/").pop()}</Td>
                  <Td>{fmtDate(o.last_modified)}</Td>
                  <Td>{fmtSize(o.size_bytes)}</Td>
                  <Td>
                    <button
                      onClick={() => getDownloadUrl(o.key)}
                      style={{ padding: "4px 10px", fontSize: 12, background: "transparent", color: "var(--auprea-navy)", border: "1px solid var(--auprea-navy)" }}
                      data-testid={`btn-download-${o.key}`}
                    >
                      URL téléchargement (1h)
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale/panneau URL téléchargement */}
      {downloadInfo && (
        <div
          data-testid="download-url-panel"
          style={{ marginTop: 14, padding: 14, background: "rgba(202,168,106,0.08)", border: "1px solid rgba(202,168,106,0.3)", borderLeft: "3px solid var(--auprea-gold)" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ fontSize: 13, color: "var(--auprea-navy)" }}>URL de téléchargement (valide 1 heure)</strong>
              {downloadInfo.loading && <p style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>Génération…</p>}
              {downloadInfo.error && <p style={{ fontSize: 12, color: "#a33b1a", marginTop: 4 }}>❌ {downloadInfo.error}</p>}
              {downloadInfo.url && (
                <>
                  <p style={{ fontSize: 12, color: "rgba(35,38,46,0.7)", marginTop: 6, marginBottom: 6 }}>
                    Fichier : <code>{downloadInfo.key}</code>
                  </p>
                  <textarea
                    readOnly
                    value={downloadInfo.url}
                    onFocus={(e) => e.target.select()}
                    style={{ width: "100%", padding: 8, fontSize: 11, fontFamily: "monospace", background: "#fff", border: "1px solid rgba(35,38,46,0.2)", minHeight: 60, resize: "vertical" }}
                    data-testid="download-url-text"
                  />
                  <p style={{ fontSize: 11, color: "rgba(35,38,46,0.55)", marginTop: 8 }}>
                    ⚠️ Fichier chiffré. Déchiffrez avec :
                    <code style={{ display: "block", background: "#fff", padding: 6, marginTop: 4, fontSize: 10 }}>
                      openssl enc -d -aes-256-cbc -pbkdf2 -pass pass:{"<BACKUP_ENCRYPTION_KEY>"} -in fichier.enc -out fichier.tar.gz
                    </code>
                  </p>
                </>
              )}
            </div>
            <button
              onClick={() => setDownloadInfo(null)}
              style={{ padding: "4px 10px", fontSize: 12, color: "rgba(35,38,46,0.6)" }}
              data-testid="btn-close-download"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ icon, label, value, testid }) {
  return (
    <div style={{ background: "rgba(21,41,79,0.04)", padding: "12px 14px", borderLeft: "3px solid var(--auprea-gold)" }} data-testid={testid}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(35,38,46,0.6)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
        {icon && React.cloneElement(icon, { size: 13 })}
        <span>{label}</span>
      </div>
      <div style={{ fontFamily: "Spectral, serif", fontSize: 24, color: "var(--auprea-navy)", fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th style={{ padding: "8px 10px", fontSize: 11, fontWeight: 600, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{children}</th>;
}
function Td({ children, style = {} }) {
  return <td style={{ padding: "8px 10px", ...style }}>{children}</td>;
}


function RestoreTestBanner({ config, onReload }) {
  const [saving, setSaving] = React.useState(false);
  const lastTest = config?.last_restore_test;
  const now = new Date();
  let daysSince = null;
  let status = "never";
  if (lastTest) {
    const t = new Date(lastTest);
    daysSince = Math.floor((now - t) / (1000 * 60 * 60 * 24));
    status = daysSince > 180 ? "overdue" : "ok";
  }

  const markTested = async () => {
    if (!window.confirm("Confirmer que vous venez de tester la procédure de restauration ?\n\n(Procédure documentée dans /app/memory/BACKUP_RESTORE.md.)")) return;
    setSaving(true);
    try {
      const notes = window.prompt("Notes (facultatif) :", "") || "";
      await api.post("/admin/backup/mark-restore-tested", { notes });
      await onReload();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const color = status === "overdue" ? "#a33b1a" : status === "ok" ? "var(--auprea-green)" : "rgba(35,38,46,0.6)";
  const bg = status === "overdue" ? "rgba(163,59,26,0.06)" : status === "ok" ? "rgba(47,125,91,0.05)" : "rgba(21,41,79,0.04)";

  return (
    <div
      data-testid="restore-test-banner"
      style={{
        padding: "12px 16px", marginBottom: 16, background: bg,
        border: `1px solid ${status === "overdue" ? "rgba(163,59,26,0.3)" : "rgba(21,41,79,0.08)"}`,
        borderLeft: `3px solid ${color}`,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}
    >
      <div style={{ flex: 1, minWidth: 220, fontSize: 13 }}>
        <strong style={{ color: "var(--auprea-navy)" }}>Test de restauration trimestriel</strong>
        {status === "never" && <div style={{ marginTop: 3, color: "rgba(35,38,46,0.7)" }}>Jamais testé manuellement. Recommandation : effectuer un test tous les 3-6 mois.</div>}
        {status === "ok" && <div style={{ marginTop: 3, color: "rgba(35,38,46,0.7)" }}>Dernier test il y a <strong>{daysSince} jour{daysSince > 1 ? "s" : ""}</strong> par {config.last_restore_test_by || "admin"} · {config.last_restore_test_notes || "sans note"}</div>}
        {status === "overdue" && <div style={{ marginTop: 3, color: "#7a1e0d" }}><strong>Dernier test il y a {daysSince} jours</strong> — au-delà de 6 mois. Un nouveau test est recommandé.</div>}
      </div>
      <button onClick={markTested} disabled={saving} data-testid="btn-mark-restore-tested" style={{ padding: "8px 14px", fontSize: 12, background: "transparent", color: "var(--auprea-navy)", border: "1px solid var(--auprea-navy)", fontWeight: 600 }}>
        {saving ? "…" : "J'ai testé la restauration"}
      </button>
    </div>
  );
}

